import type { FC, ReactNode, InputHTMLAttributes, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react'

import { useId } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

interface FieldShellProps {
    label: string
    hint?: string
    error?: string | null
    className?: string
    children: (id: string, describedBy: string | undefined) => ReactNode
    optional?: boolean
}

export const FieldShell: FC<FieldShellProps> = ({ label, hint, error, className, children, optional }): ReactNode => {
    const id = useId()
    const hintId = hint ? `${id}-hint` : undefined
    const errorId = error ? `${id}-error` : undefined
    const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined

    return (
        <div className={cn('flex flex-col gap-1.5', className)}>
            <label htmlFor={id} className='text-[0.8rem] font-medium text-foreground/85'>
                {label}
                {optional && <span className='ml-1 font-normal text-muted-foreground'>(optioneel)</span>}
            </label>

            {children(id, describedBy)}

            {hint && !error && (
                <p id={hintId} className='text-xs text-muted-foreground'>
                    {hint}
                </p>
            )}

            {error && (
                <p id={errorId} className='text-xs text-destructive' role='alert'>
                    {error}
                </p>
            )}
        </div>
    )
}

const controlClass = 'w-full rounded-md border border-input bg-card px-3 text-sm text-foreground shadow-xs transition-colors outline-none placeholder:text-muted-foreground/80 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30 disabled:cursor-not-allowed disabled:opacity-60 aria-invalid:border-destructive'

interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
    label: string
    hint?: string
    error?: string | null
    containerClassName?: string
    optional?: boolean
}

export const TextField: FC<TextFieldProps> = ({ label, hint, error, containerClassName, className, optional, ...props }): ReactNode => {
    return (
        <FieldShell label={label} hint={hint} error={error} className={containerClassName} optional={optional}>
            {(id, describedBy) => (
                <input
                    id={id}
                    aria-describedby={describedBy}
                    aria-invalid={error ? true : undefined}
                    className={cn(controlClass, 'h-11', className)}
                    {...props}
                />
            )}
        </FieldShell>
    )
}

interface TextAreaFieldProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
    label: string
    hint?: string
    error?: string | null
    containerClassName?: string
    optional?: boolean
}

export const TextAreaField: FC<TextAreaFieldProps> = ({ label, hint, error, containerClassName, className, optional, ...props }): ReactNode => {
    return (
        <FieldShell label={label} hint={hint} error={error} className={containerClassName} optional={optional}>
            {(id, describedBy) => (
                <textarea
                    id={id}
                    aria-describedby={describedBy}
                    aria-invalid={error ? true : undefined}
                    className={cn(controlClass, 'min-h-28 py-2.5 leading-relaxed', className)}
                    {...props}
                />
            )}
        </FieldShell>
    )
}

interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
    label: string
    hint?: string
    error?: string | null
    containerClassName?: string
    options: { value: string, label: string }[]
    optional?: boolean
}

export const SelectField: FC<SelectFieldProps> = ({ label, hint, error, containerClassName, className, options, optional, ...props }): ReactNode => {
    return (
        <FieldShell label={label} hint={hint} error={error} className={containerClassName} optional={optional}>
            {(id, describedBy) => (
                <div className='relative'>
                    <select
                        id={id}
                        aria-describedby={describedBy}
                        aria-invalid={error ? true : undefined}
                        className={cn(controlClass, 'h-11 appearance-none pr-9', className)}
                        {...props}
                    >
                        {options.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>

                    <ChevronDown className='pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-muted-foreground' aria-hidden='true' />
                </div>
            )}
        </FieldShell>
    )
}

interface CheckboxFieldProps {
    checked: boolean
    onChange: (checked: boolean) => void
    children: ReactNode
    className?: string
    disabled?: boolean
}

export const CheckboxField: FC<CheckboxFieldProps> = ({ checked, onChange, children, className, disabled }): ReactNode => {
    const id = useId()

    return (
        <div className={cn('flex items-start gap-3', className)}>
            <input
                id={id}
                type='checkbox'
                checked={checked}
                disabled={disabled}
                onChange={event => onChange(event.target.checked)}
                className='mt-0.5 size-4.5 shrink-0 cursor-pointer rounded-sm border-input accent-[var(--primary)]'
            />

            <label htmlFor={id} className='cursor-pointer text-sm leading-snug text-foreground/85'>
                {children}
            </label>
        </div>
    )
}
